import { IActionButton } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { Loading, SidebarOpenedIcon } from '@schooly/style';
import { createContext, FC, PropsWithChildren, useContext, useState } from 'react';

import { useSchool } from '../../../hooks/useSchool';
import Sidebar from '../Sidebar/Sidebar';
import { MainLayoutContainer, MainLayoutContent, MainLayoutStyled } from './MainLayout.styled';
import { MainLayoutEmptyStub } from './MainLayoutEmptyStub';
import {
  SIDEBAR_COLLAPSED_STORAGE_KEY,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
} from '../Sidebar/Sidebar.styled';
import { useTimetablePermissions } from 'apps/web/src/hooks/useTimetablePermissions';
import {
  allowRenderSchoolLegalEntities,
  allowRenderSchoolProducts,
} from 'apps/web/src/helpers/renderRoutesByPermissions';
import { IconButton } from '@mui/material';

interface MainLayoutProps extends PropsWithChildren {
  pageTitleTextId?: string;
  className?: string;
  actionButtons?: IActionButton[];
}

type LayoutSidebarContext = {
  isCollapsed: boolean;
  sidebarWidth: number;
};

const LayoutSidebarContext = createContext<LayoutSidebarContext>({
  isCollapsed: false,
  sidebarWidth: SIDEBAR_WIDTH,
});

const MainLayout: FC<MainLayoutProps> = ({ className, children }) => {
  // We're utilizing flag instead changeLastSchool.isLoading to avoid showing outdated ui
  // before window.location.href performs
  const { currentUser, currentSchool, permissions } = useAuth();
  const { hasSystemRole, isFetching, isSchoolAdmin, isSchoolChanging, isSchoolPath } = useSchool();
  const { canManageTimetables } = useTimetablePermissions();

  const hasTimetablesAccess = canManageTimetables;
  const hasLegalEntitiesAccess = allowRenderSchoolLegalEntities(permissions);
  const hasSchoolProductsAccess = allowRenderSchoolProducts(permissions);
  const showSchoolMenu =
    isSchoolPath &&
    (isSchoolAdmin || hasTimetablesAccess || hasLegalEntitiesAccess || hasSchoolProductsAccess);

  const [isSidebarCollapsed, setSidebarCollapsed] = useState(
    !!localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY),
  );

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((v) => {
      !v
        ? localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, 'true')
        : localStorage.removeItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
      return !v;
    });
  };

  const sidebarCollapsable = !showSchoolMenu;
  const sidebarCollapsed = sidebarCollapsable && isSidebarCollapsed;

  const renderContent = () => {
    if (currentUser === undefined || isFetching || isSchoolChanging) {
      return <Loading />;
    }

    if (!currentSchool || (!isSchoolAdmin && !hasSystemRole)) {
      return <MainLayoutEmptyStub />;
    }

    return children;
  };

  return (
    <LayoutSidebarContext.Provider
      value={{
        sidebarWidth: sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
        isCollapsed: sidebarCollapsed,
      }}
    >
      <MainLayoutStyled sidebarCollapsed={sidebarCollapsed} className={className}>
        <Sidebar isCollapsed={sidebarCollapsed} showSchoolMenu={showSchoolMenu} />

        <MainLayoutContainer>
          <MainLayoutContent>{renderContent()}</MainLayoutContent>
        </MainLayoutContainer>
        {sidebarCollapsable && (
          <IconButton
            sx={(theme) => ({
              position: 'fixed',
              top: `calc(${theme.spacing(8)} + var(--trial-banner-height, 0px))`,
              zIndex: 1,
              left: (sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH) - 16,
              '&:hover svg': {
                path: {
                  fill: theme.palette.primary.main,
                },
              },
              '.svg-icon': {
                filter: 'drop-shadow(0px 1px 4px #25275A20)',
              },
              fontSize: theme.spacing(3.5),
              transform: sidebarCollapsed ? 'rotate(180deg)' : 0,
            })}
            onClick={toggleSidebarCollapsed}
          >
            <SidebarOpenedIcon className="reset-svg-currentColor" />
          </IconButton>
        )}
      </MainLayoutStyled>
    </LayoutSidebarContext.Provider>
  );
};

export const useSidebarLayout = () => {
  return useContext(LayoutSidebarContext);
};

export default MainLayout;
